// Business Rules Settings Tab
import React from "react";
import SettingsSaveButtons from "./SettingsSaveButtons";

const BusinessRulesSettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    saveSettings();
  };

  // Helper to update nested payoutSettings fields
  const updatePayoutField = (field, value) => {
    updateSettings({
      payoutSettings: {
        ...(settings.payoutSettings || {}),
        [field]: value,
      },
    });
  };

  // Helper to update nested refundSettings fields
  const updateRefundField = (field, value) => {
    updateSettings({
      refundSettings: {
        ...(settings.refundSettings || {}),
        [field]: value,
      },
    });
  };

  // Helper to update nested refundSettings.adminApprovalRiskRules fields
  const updateRefundRiskRuleField = (field, value) => {
    updateSettings({
      refundSettings: {
        ...(settings.refundSettings || {}),
        adminApprovalRiskRules: {
          ...(settings.refundSettings?.adminApprovalRiskRules || {}),
          [field]: value,
        },
      },
    });
  };

  // Cancellation fee distribution is retired (2026-11-01): a cancellation now divides by each professional's own
  // commission and the platform takes nothing, so there is no platform-wide ratio left to edit. The setter is
  // removed rather than left unused, so nothing can quietly wire a new input back to it.

  const updateReschedulePolicyField = (field, value) => {
    updateSettings({
      reschedulePolicy: {
        ...(settings.reschedulePolicy || {}),
        [field]: value,
      },
    });
  };

  // Cancellation ladder. These figures decide how much of a booking is retained, so the
  // editor mirrors the server's rules (validateCancellationPolicy) rather than trusting
  // the operator's typing.
  const cancellationPolicy = settings.cancellationPolicy || {};
  const cancellationFeeTiers = Array.isArray(cancellationPolicy.feeTiers)
    ? cancellationPolicy.feeTiers
    : [];
  const isCustomCancellationPolicy =
    cancellationPolicy.policyType === "custom";

  const updateCancellationPolicyField = (field, value) => {
    updateSettings({
      cancellationPolicy: { ...cancellationPolicy, [field]: value },
    });
  };

  const setCancellationFeeTiers = (feeTiers) =>
    updateCancellationPolicyField("feeTiers", feeTiers);

  const updateCancellationFeeTier = (index, field, value) =>
    setCancellationFeeTiers(
      cancellationFeeTiers.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, [field]: value } : tier,
      ),
    );

  const removeCancellationFeeTier = (index) =>
    setCancellationFeeTiers(
      cancellationFeeTiers.filter((_, tierIndex) => tierIndex !== index),
    );

  const addCancellationFeeTier = () => {
    const highestHours = cancellationFeeTiers.reduce(
      (highest, tier) => Math.max(highest, Number(tier.minHours) || 0),
      0,
    );
    setCancellationFeeTiers([
      ...cancellationFeeTiers,
      { minHours: highestHours + 24, feePercent: 0 },
    ]);
  };

  // The ladder OGESTYLE enforces today, offered as a starting point.
  const STANDARD_CANCELLATION_TIERS = [
    { minHours: 0, feePercent: 100 },
    { minHours: 24, feePercent: 75 },
    { minHours: 48, feePercent: 50 },
    { minHours: 96, feePercent: 0 },
  ];

  const sortedCancellationFeeTiers = [...cancellationFeeTiers].sort(
    (a, b) => Number(a.minHours) - Number(b.minHours),
  );

  const cancellationTierProblems = (() => {
    const problems = [];
    const seenHours = new Set();
    sortedCancellationFeeTiers.forEach((tier) => {
      const hours = Number(tier.minHours);
      const fee = Number(tier.feePercent);
      if (!Number.isFinite(hours) || hours < 0) {
        problems.push("Every band needs a start of 0 hours or more.");
      } else if (seenHours.has(hours)) {
        problems.push(`${hours} hours is used by more than one band.`);
      } else {
        seenHours.add(hours);
      }
      if (!Number.isFinite(fee) || fee < 0 || fee > 100) {
        problems.push("Retention must be between 0% and 100%.");
      }
    });
    if (sortedCancellationFeeTiers.length === 0 && isCustomCancellationPolicy) {
      problems.push("A custom policy needs at least one band.");
    }
    if (sortedCancellationFeeTiers.length > 0 && !seenHours.has(0)) {
      problems.push(
        "Add a band starting at 0 hours — otherwise a last-minute cancellation falls through to another band's rate.",
      );
    }
    return [...new Set(problems)];
  })();

  // What the customer is actually charged, in plain English. The calculator applies the
  // band with the highest minHours that the booking is still at or above.
  const describeCancellationBand = (tier, nextTier) => {
    const fee = Number(tier.feePercent);
    const from = Number(tier.minHours);
    if (!nextTier) {
      return `${from} hours or more before the appointment — ${fee}% retained`;
    }
    const to = Number(nextTier.minHours);
    return `${from} to ${to} hours before — ${fee}% retained`;
  };

  const cancellationTierSummary = sortedCancellationFeeTiers.map((tier, index) =>
    describeCancellationBand(tier, sortedCancellationFeeTiers[index + 1]),
  );

  const formatPercent = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "";
    return Number(numeric.toFixed(2));
  };

  const parsePercent = (value) => {
    if (value === "") return "";
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "";
    return Number(numeric.toFixed(2));
  };

  // Helper to update nested schedulingSettings fields
  const updateSchedulingField = (field, value) => {
    updateSettings({
      schedulingSettings: {
        ...(settings.schedulingSettings || {}),
        [field]: value,
      },
    });
  };

  // Helper to update nested bookingDefaults fields
  const updateBookingDefaultsField = (field, value) => {
    updateSettings({
      bookingDefaults: {
        ...(settings.bookingDefaults || {}),
        [field]: value,
      },
    });
  };

  // Helper to update nested retentionSettings fields
  const updateRetentionField = (field, value) => {
    updateSettings({
      retentionSettings: {
        ...(settings.retentionSettings || {}),
        [field]: value,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          Product Media Limits
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Control how many images product owners can upload per product.
        </p>

        <div style={{ maxWidth: "280px" }}>
          <label
            style={{
              display: "block",
              fontWeight: "500",
              marginBottom: "8px",
            }}
          >
            Max Product Images Per Product
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={settings.maxProductImagesPerProduct ?? 8}
            onChange={(e) =>
              updateSettings({
                maxProductImagesPerProduct: parseInt(e.target.value, 10) || 1,
              })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
        </div>
      </div>

      {/* Payout Settings Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          💸 Rolling Weekly Payout Settings
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure rolling weekly payouts (beneficiaries receive payouts on assigned weekdays).
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Minimum Payout ($)
            </label>
            <input
              type="number"
              min="1"
              max="500"
              value={settings.payoutSettings?.minimumPayoutAmount ?? 25}
              onChange={(e) =>
                updatePayoutField(
                  "minimumPayoutAmount",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Earnings Hold Time (Hours)
            </label>
            <input
              type="number"
              min="24"
              max="168"
              value={settings.payoutSettings?.earningsHoldHours ?? 72}
              onChange={(e) =>
                updatePayoutField(
                  "earningsHoldHours",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Hold time before releasing earnings (unified for all roles)
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Service Post-Completion Hold (Minutes)
            </label>
            <input
              type="number"
              min="0"
              max="180"
              value={settings.payoutSettings?.servicePostCompletionHoldMinutes ?? 180}
              onChange={(e) =>
                updatePayoutField(
                  "servicePostCompletionHoldMinutes",
                  parseInt(e.target.value, 10),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Additional hold after service completion before payout eligibility
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Product Post-Fulfillment Hold (Minutes)
            </label>
            <input
              type="number"
              min="0"
              max="180"
              value={settings.payoutSettings?.productPostFulfillmentHoldMinutes ?? 180}
              onChange={(e) =>
                updatePayoutField(
                  "productPostFulfillmentHoldMinutes",
                  parseInt(e.target.value, 10),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Additional hold after fulfillment confirmation before payout eligibility
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Product Dispute Buffer (Hours)
            </label>
            <input
              type="number"
              min="0"
              max="168"
              value={settings.payoutSettings?.productDisputeBufferHours ?? 24}
              onChange={(e) =>
                updatePayoutField(
                  "productDisputeBufferHours",
                  parseInt(e.target.value, 10),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Minimum post-pickup hold window used by the pickup hook to reduce chargeback risk
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Retry Failed Payouts After (Hours)
            </label>
            <input
              type="number"
              min="1"
              max="24"
              value={settings.payoutSettings?.retryFailedPayoutsAfterHours ?? 6}
              onChange={(e) =>
                updatePayoutField(
                  "retryFailedPayoutsAfterHours",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              How often to retry payouts that failed due to insufficient balance
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Max Retry Attempts
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={settings.payoutSettings?.maxRetryAttempts ?? 5}
              onChange={(e) =>
                updatePayoutField(
                  "maxRetryAttempts",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Maximum retry attempts before marking payout as failed
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Payout Day Change Cooldown (Days)
            </label>
            <input
              type="number"
              min="0"
              max="90"
              value={settings.payoutSettings?.payoutDayChangeCooldownDays ?? 30}
              onChange={(e) =>
                updatePayoutField(
                  "payoutDayChangeCooldownDays",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Minimum days between weekday changes for beneficiaries
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Instant Payout Fee (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="10"
              value={settings.payoutSettings?.instantPayoutFeePercent ?? 1.5}
              onChange={(e) =>
                updatePayoutField(
                  "instantPayoutFeePercent",
                  parseFloat(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Fee charged for instant payout requests (any day)
            </p>
          </div>

          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "500",
              }}
            >
              <input
                type="checkbox"
                checked={
                  settings.payoutSettings?.instantPayoutEnabled !== false
                }
                onChange={(e) =>
                  updatePayoutField("instantPayoutEnabled", e.target.checked)
                }
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              Enable Instant Payout Feature
            </label>
            <p
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginTop: "4px",
                marginLeft: "26px",
              }}
            >
              Allow professionals to request instant payouts for a fee (any day)
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Min Hours Before Instant Payout
            </label>
            <input
              type="number"
              min="0"
              max="168"
              value={settings.payoutSettings?.minHoursBeforeInstantPayout ?? 1}
              onChange={(e) =>
                updatePayoutField(
                  "minHoursBeforeInstantPayout",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              <strong>Additional</strong> hours after earnings release before instant payout allowed (fraud prevention)
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Payout Timezone
            </label>
            <select
              value={settings.payoutSettings?.payoutTimezone ?? "America/Chicago"}
              onChange={(e) =>
                updatePayoutField("payoutTimezone", e.target.value)
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <option value="America/Chicago">America/Chicago (CST)</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
              <option value="America/Denver">America/Denver (MST)</option>
              <option value="UTC">UTC</option>
            </select>
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Timezone for determining current weekday during payout processing
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Temporary Liability Reserve (%)
            </label>
            <input
              type="number"
              min="0"
              max="25"
              step="0.1"
              value={settings.payoutSettings?.temporaryLiabilityReservePercent ?? 0}
              onChange={(e) =>
                updatePayoutField(
                  "temporaryLiabilityReservePercent",
                  parseFloat(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Temporary reserve during rollout to reduce residual refund-loss risk
            </p>
          </div>

          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "500",
              }}
            >
              <input
                type="checkbox"
                checked={settings.payoutSettings?.negativeBalanceOffsetEnabled !== false}
                onChange={(e) =>
                  updatePayoutField("negativeBalanceOffsetEnabled", e.target.checked)
                }
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              Enable Negative Balance Offset Recovery
            </label>
            <p
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginTop: "4px",
                marginLeft: "26px",
              }}
            >
              Allows post-payout refund liabilities to be recovered from future eligible payouts
            </p>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Enabled Payout Weekdays
            </label>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                const isEnabled = (settings.payoutSettings?.enabledPayoutWeekdays || [
                  "monday", "tuesday", "wednesday", "thursday", "friday"
                ]).includes(day);
                
                return (
                  <label
                    key={day}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "8px 12px",
                      border: `2px solid ${isEnabled ? "#2563eb" : "#d1d5db"}`,
                      borderRadius: "6px",
                      cursor: "pointer",
                      background: isEnabled ? "#eff6ff" : "white",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: isEnabled ? "#2563eb" : "#6b7280",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => {
                        const currentWeekdays = settings.payoutSettings?.enabledPayoutWeekdays || [
                          "monday", "tuesday", "wednesday", "thursday", "friday"
                        ];
                        const newWeekdays = e.target.checked
                          ? [...currentWeekdays, day]
                          : currentWeekdays.filter((d) => d !== day);
                        
                        // Require at least one weekday enabled
                        if (newWeekdays.length === 0) {
                          alert("At least one weekday must be enabled");
                          return;
                        }
                        
                        updatePayoutField("enabledPayoutWeekdays", newWeekdays);
                      }}
                      style={{ width: "16px", height: "16px", cursor: "pointer" }}
                    />
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </label>
                );
              })}
            </div>
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px" }}>
              Select which weekdays can be used for automatic payout execution (at least one required)
            </p>
          </div>
        </div>
      </div>

      {/* Refund Governance Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🛡️ Refund Governance Controls
        </h2>
        <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "24px" }}>
          Configure platform-admin controls for high-risk refund decisions and response SLAs.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "500",
              }}
            >
              <input
                type="checkbox"
                checked={settings.refundSettings?.platformAdminApprovalEnabled !== false}
                onChange={(e) =>
                  updateRefundField("platformAdminApprovalEnabled", e.target.checked)
                }
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              Require Admin Approval For High-Risk Refunds
            </label>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Admin Approval Threshold Amount ($)
            </label>
            <input
              type="number"
              min="0"
              max="10000"
              value={settings.refundSettings?.adminApprovalThresholdAmount ?? 150}
              onChange={(e) =>
                updateRefundField(
                  "adminApprovalThresholdAmount",
                  parseFloat(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Admin Refund SLA (Minutes)
            </label>
            <input
              type="number"
              min="5"
              max="1440"
              value={settings.refundSettings?.adminRefundSlaMinutes ?? 120}
              onChange={(e) =>
                updateRefundField(
                  "adminRefundSlaMinutes",
                  parseInt(e.target.value, 10),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "500",
              }}
            >
              <input
                type="checkbox"
                checked={settings.refundSettings?.allowAdminForceRefund !== false}
                onChange={(e) =>
                  updateRefundField("allowAdminForceRefund", e.target.checked)
                }
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              Allow Admin Force Approve
            </label>
          </div>

          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "500",
              }}
            >
              <input
                type="checkbox"
                checked={settings.refundSettings?.allowAdminForceDeny !== false}
                onChange={(e) =>
                  updateRefundField("allowAdminForceDeny", e.target.checked)
                }
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              Allow Admin Force Deny
            </label>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "12px",
              }}
            >
              Risk Rules Requiring Admin Approval
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={
                    settings.refundSettings?.adminApprovalRiskRules?.alreadyTransferredPayout !==
                    false
                  }
                  onChange={(e) =>
                    updateRefundRiskRuleField(
                      "alreadyTransferredPayout",
                      e.target.checked,
                    )
                  }
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
                Already Transferred Payout
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={
                    settings.refundSettings?.adminApprovalRiskRules?.repeatedCancellations !==
                    false
                  }
                  onChange={(e) =>
                    updateRefundRiskRuleField(
                      "repeatedCancellations",
                      e.target.checked,
                    )
                  }
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
                Repeated Cancellation Pattern
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={
                    settings.refundSettings?.adminApprovalRiskRules?.lateCancellationWindow !==
                    false
                  }
                  onChange={(e) =>
                    updateRefundRiskRuleField(
                      "lateCancellationWindow",
                      e.target.checked,
                    )
                  }
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
                Late Cancellation Window
              </label>
            </div>
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "10px" }}>
              These controls define when refund requests must be reviewed by a platform admin.
            </p>
          </div>
        </div>
      </div>

      {/* Cancellation Policy — the ladder itself. The distribution panel below explains how a
          retained amount is divided; this one decides how much is retained. */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          ⏱️ Cancellation Policy
        </h2>
        <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "24px" }}>
          How much of a booked appointment is retained, depending on how long
          before the appointment the customer cancels. These are the figures
          customers see in the cancellation policy dialog.
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px",
          }}
        >
          <input
            type="checkbox"
            checked={cancellationPolicy.enabled !== false}
            onChange={(e) =>
              updateCancellationPolicyField("enabled", e.target.checked)
            }
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          Charge a cancellation fee
        </label>

        <div
          style={{
            display: "grid",
            gap: "20px",
            gridTemplateColumns: "1fr 1fr",
            marginBottom: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Policy Type
            </label>
            <select
              value={cancellationPolicy.policyType || "custom"}
              onChange={(e) =>
                updateCancellationPolicyField("policyType", e.target.value)
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
              }}
            >
              <option value="custom">Custom — use the bands below</option>
              <option value="free">Free cancellation any time</option>
              <option value="24-hour">Free if more than 24 hours before</option>
              <option value="48-hour">Free if more than 48 hours before</option>
              <option value="72-hour">Free if more than 72 hours before</option>
              <option value="strict">No refunds</option>
            </select>
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}>
              &ldquo;Free cancellation any time&rdquo; and &ldquo;No
              refunds&rdquo; ignore the bands below.
            </p>
          </div>
          {["24-hour", "48-hour", "72-hour"].includes(
            cancellationPolicy.policyType,
          ) && (
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Late Cancellation Fee (%)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={formatPercent(cancellationPolicy.cancellationFeePercent)}
                onChange={(e) =>
                  updateCancellationPolicyField(
                    "cancellationFeePercent",
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                placeholder="50"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                }}
              />
              <p
                style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}
              >
                Charged once the free window has passed.
              </p>
            </div>
          )}
        </div>

        {!isCustomCancellationPolicy &&
          cancellationFeeTiers.length > 0 &&
          ["24-hour", "48-hour", "72-hour"].includes(
            cancellationPolicy.policyType,
          ) && (
            <div
              style={{
                padding: "12px 16px",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <p style={{ fontSize: "13px", color: "#92400e", margin: 0 }}>
                Bands are saved, and saved bands take precedence over the policy
                type above. Switch the type to Custom if the bands are what you
                intend, or remove them to use the single late fee.
              </p>
            </div>
          )}

        <h3
          style={{ fontSize: "16px", fontWeight: "600", marginBottom: "6px" }}
        >
          Retention by time before the appointment
        </h3>
        <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>
          Each band applies from its start time up to the next band&apos;s start.
          The last band applies to anything further out. One band must start at 0
          hours so a last-minute cancellation still has a rate.
        </p>

        {cancellationFeeTiers.length === 0 ? (
          <div
            style={{
              padding: "16px",
              background: "#f9fafb",
              border: "1px dashed #d1d5db",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
              No bands saved. {isCustomCancellationPolicy
                ? "A custom policy needs at least one — customers would otherwise be charged the platform default."
                : ""}
            </p>
          </div>
        ) : (
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 90px",
                gap: "12px",
                fontSize: "12px",
                fontWeight: "600",
                color: "#6b7280",
                paddingBottom: "6px",
              }}
            >
              <span>Starts at (hours before)</span>
              <span>Retained (%)</span>
              <span />
            </div>
            {cancellationFeeTiers.map((tier, index) => (
              <div
                key={`cancellation-tier-${index}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 90px",
                  gap: "12px",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={tier.minHours ?? ""}
                  onChange={(e) =>
                    updateCancellationFeeTier(
                      index,
                      "minHours",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                  }}
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={tier.feePercent ?? ""}
                  onChange={(e) =>
                    updateCancellationFeeTier(
                      index,
                      "feePercent",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeCancellationFeeTier(index)}
                  style={{
                    padding: "10px",
                    background: "#fef2f2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
          <button
            type="button"
            onClick={addCancellationFeeTier}
            style={{
              padding: "10px 16px",
              background: "#eff6ff",
              color: "#1d4ed8",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            + Add band
          </button>
          <button
            type="button"
            onClick={() => setCancellationFeeTiers(STANDARD_CANCELLATION_TIERS)}
            style={{
              padding: "10px 16px",
              background: "white",
              color: "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Load standard ladder (100 / 75 / 50 / 0%)
          </button>
        </div>

        {cancellationTierProblems.length > 0 && (
          <div
            style={{
              padding: "12px 16px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            {cancellationTierProblems.map((problem) => (
              <p
                key={problem}
                style={{ fontSize: "13px", color: "#b91c1c", margin: 0 }}
              >
                {problem}
              </p>
            ))}
          </div>
        )}

        {cancellationTierSummary.length > 0 && (
          <div
            style={{
              padding: "16px",
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
          >
            <p
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              A customer cancelling will see:
            </p>
            {cancellationTierSummary.map((line) => (
              <p
                key={line}
                style={{ fontSize: "13px", color: "#374151", margin: "2px 0" }}
              >
                • {line}
              </p>
            ))}
          </div>
        )}
      </div>



      {/* Cancellation Fee Distribution — RETIRED (2026-11-01).

          The owner's decision: a cancelled appointment is treated as if the service took place, so the money is
          divided exactly as the booking already divides it — by each professional's own commission — and the
          platform takes nothing from it. This panel used to offer a platform-wide 5% / 25% / 70% table, which paid
          a shop 25% of a booking it had agreed 42% on and took 5% of money the platform does not share in.

          The inputs are replaced by a note rather than deleted, so an operator who remembers them can see WHY
          they are gone instead of hunting a setting that has quietly stopped working. */}
      <div
        style={{
          background: "#f0fdf4",
          border: "1px solid #86efac",
          borderRadius: "12px",
          padding: "24px 32px",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}>
          Cancellation fees follow the commission
        </h2>
        <p style={{ fontSize: "14px", color: "#166534", margin: 0 }}>
          A cancelled appointment is divided exactly as a completed one: each
          professional&#39;s commission (set per professional under Professionals) decides
          the split, an independent professional keeps 100%, and when the owner performs
          the service it all goes to the business. The platform takes nothing. A retained
          tip goes to the professional whole. There is no platform-wide ratio to configure.
        </p>
      </div>


      {/* Rescheduling Fee Settings Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🔁 Rescheduling Fee Settings
        </h2>
        <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "24px" }}>
          Configure late customer reschedule fees and how those fees are split.
        </p>

        <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
          <input
            type="checkbox"
            checked={settings.reschedulePolicy?.enabled !== false}
            onChange={(e) => updateReschedulePolicyField("enabled", e.target.checked)}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          Enable paid late rescheduling
        </label>

        <div
          style={{
            display: "grid",
            gap: "20px",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Late Reschedule Fee (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formatPercent(settings.reschedulePolicy?.lateRescheduleFeePercent)}
              onChange={(e) =>
                updateReschedulePolicyField(
                  "lateRescheduleFeePercent",
                  parsePercent(e.target.value),
                )
              }
              placeholder="Required"
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Percentage of service subtotal charged within the 48-hour reschedule window
            </p>
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Platform Share of Reschedule Fee (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formatPercent(
                settings.reschedulePolicy?.platformSharePercentOfFee ?? 50,
              )}
              onChange={(e) =>
                updateReschedulePolicyField(
                  "platformSharePercentOfFee",
                  parsePercent(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Remaining fee amount follows the provider split rules for R3, R4, and R7
            </p>
          </div>
        </div>
      </div>

      {/* Scheduling Settings Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          📅 Default Scheduling Settings
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure default booking and scheduling options.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Default Opening Time
            </label>
            <input
              type="time"
              value={
                settings.schedulingSettings?.defaultWorkingHoursStart ?? "09:00"
              }
              onChange={(e) =>
                updateSchedulingField(
                  "defaultWorkingHoursStart",
                  e.target.value,
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Default Closing Time
            </label>
            <input
              type="time"
              value={
                settings.schedulingSettings?.defaultWorkingHoursEnd ?? "18:00"
              }
              onChange={(e) =>
                updateSchedulingField("defaultWorkingHoursEnd", e.target.value)
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Default Slot Duration (Mins)
            </label>
            <select
              value={
                settings.schedulingSettings?.defaultSlotDurationMinutes ?? 30
              }
              onChange={(e) =>
                updateSchedulingField(
                  "defaultSlotDurationMinutes",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
              <option value={90}>90 minutes</option>
              <option value={120}>120 minutes</option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Max Advance Booking (Days)
            </label>
            <input
              type="number"
              min="7"
              max="365"
              value={settings.schedulingSettings?.maxAdvanceBookingDays ?? 90}
              onChange={(e) =>
                updateSchedulingField(
                  "maxAdvanceBookingDays",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Min Booking Notice (Hours)
            </label>
            <input
              type="number"
              min="0"
              max="72"
              value={settings.schedulingSettings?.minBookingNoticeHours ?? 2}
              onChange={(e) =>
                updateSchedulingField(
                  "minBookingNoticeHours",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              paddingTop: "28px",
            }}
          >
            <input
              type="checkbox"
              id="allowSameDayBooking"
              checked={
                settings.schedulingSettings?.allowSameDayBooking !== false
              }
              onChange={(e) =>
                updateSchedulingField("allowSameDayBooking", e.target.checked)
              }
              style={{ width: "20px", height: "20px" }}
            />
            <label htmlFor="allowSameDayBooking" style={{ fontWeight: "500" }}>
              Allow Same-Day Booking
            </label>
          </div>
        </div>
      </div>

      {/* Waitlist Settings Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🧾 Waitlist Settings
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure how long appointment waitlist requests remain active by default.
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "4px 0 20px 0",
          }}
        >
          <input
            type="checkbox"
            id="waitlistManualActionsEnabled"
            checked={
              settings.bookingDefaults?.waitlistManualActionsEnabled === true
            }
            onChange={(e) =>
              updateBookingDefaultsField(
                "waitlistManualActionsEnabled",
                e.target.checked,
              )
            }
            style={{ width: "20px", height: "20px" }}
          />
          <label
            htmlFor="waitlistManualActionsEnabled"
            style={{ fontWeight: "500" }}
          >
            Enable manual waitlist actions (Notify, Convert, Priority)
          </label>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Default Waitlist Duration (Days)
            </label>
            <input
              type="number"
              min="1"
              max="60"
              value={settings.bookingDefaults?.waitlistDefaultDurationDays ?? 14}
              onChange={(e) =>
                updateBookingDefaultsField(
                  "waitlistDefaultDurationDays",
                  parseInt(e.target.value || "14", 10),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Recommended: 14 days (2 weeks)
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Maximum Waitlist Duration (Days)
            </label>
            <input
              type="number"
              min="1"
              max="180"
              value={settings.bookingDefaults?.waitlistMaxDurationDays ?? 30}
              onChange={(e) =>
                updateBookingDefaultsField(
                  "waitlistMaxDurationDays",
                  parseInt(e.target.value || "30", 10),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              Used to cap custom "wait until" dates.
            </p>
          </div>
        </div>
      </div>

      {/* Data Retention Settings Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🗄️ Data Retention Settings
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure how long data is retained in the system.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Booking History (Days)
            </label>
            <input
              type="number"
              min="365"
              max="2555"
              value={settings.retentionSettings?.bookingHistoryDays ?? 730}
              onChange={(e) =>
                updateRetentionField(
                  "bookingHistoryDays",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Default: 730 days (2 years)
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Transaction History (Days)
            </label>
            <input
              type="number"
              min="730"
              max="3650"
              value={settings.retentionSettings?.transactionHistoryDays ?? 2555}
              onChange={(e) =>
                updateRetentionField(
                  "transactionHistoryDays",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Default: 2555 days (7 years for tax)
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Inactive Account (Days)
            </label>
            <input
              type="number"
              min="90"
              max="730"
              value={settings.retentionSettings?.inactiveAccountDays ?? 365}
              onChange={(e) =>
                updateRetentionField(
                  "inactiveAccountDays",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Days before marking account inactive
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Deleted Account Retention (Days)
            </label>
            <input
              type="number"
              min="7"
              max="90"
              value={
                settings.retentionSettings?.deletedAccountRetentionDays ?? 30
              }
              onChange={(e) =>
                updateRetentionField(
                  "deletedAccountRetentionDays",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Days before permanent removal
            </p>
          </div>
        </div>
      </div>

      {/* Save Buttons */}
      <SettingsSaveButtons
        onSave={handleSubmit}
        onCancel={resetSettings}
        saving={saving}
        hasChanges={hasUnsavedChanges}
      />

      {/* Info Box */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          background: "#eff6ff",
          border: "1px solid #3b82f6",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#1e40af",
        }}
      >
        <strong>ℹ️ Important Notes:</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
          <li>Payout holds provide dispute resolution windows</li>
          <li>
            Cancellation fee splits should total 100% within each category
          </li>
          <li>
            Scheduling defaults can be overridden by individual businesses
          </li>
          <li>Data retention complies with GDPR and tax regulations</li>
        </ul>
      </div>
    </form>
  );
};

export default BusinessRulesSettings;
